import {
  benchlingWorklistFailure,
  benchlingWorklistFailureMessage,
  createBenchlingWorklist,
} from '@/components/Animals/AnimalsTable/BenchlingWorklist.utils';
import BenchlingLinkRequiredToast from '@/components/Account/BenchlingAuth/BenchlingLinkRequiredToast/BenchlingLinkRequiredToast';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useState } from 'react';
import { useForm } from 'react-hook-form';

export interface CreateBenchlingWorklistProps {
  studyApiId: StudyApiId;
  animals: Array<Animal>;
  closeModal: () => void;
}

interface BenchlingWorklistFormData {
  name: string;
}

const CreateBenchlingWorklist: FC<CreateBenchlingWorklistProps> = ({ studyApiId, animals, closeModal }) => {
  const [isCreating, setIsCreating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BenchlingWorklistFormData>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const onSubmit = async (data: BenchlingWorklistFormData): Promise<void> => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    try {
      const worklist = await createBenchlingWorklist(studyApiId, data.name.trim(), animals);
      successToast(`Created Benchling worklist "${worklist.name}" with ${worklist.item_count} animals`);
      closeModal();
    } catch (error) {
      setIsCreating(false);

      const failure = benchlingWorklistFailure(error);
      if (_notNil(failure)) {
        if (failure.reason === 'benchling_link_required') {
          errorToast(({ closeToast }) => <BenchlingLinkRequiredToast onDismiss={closeToast} />, {
            closeOnClick: false,
            autoClose: 15_000,
          });
          closeModal();
          return;
        }

        errorToast(benchlingWorklistFailureMessage(failure));
        if (failure.reason === 'benchling_request_rejected') {
          ExceptionHandler.captureException(
            new InVivoError('Benchling rejected a worklist request', {
              cause: error,
              slug: 'benchling-worklist-create',
            })
          );
        }
        return;
      }

      errorToast('Could not create the Benchling worklist, please try again later.');
      ExceptionHandler.captureException(
        new InVivoError('Could not create Benchling worklist', {
          cause: error,
          slug: 'benchling-worklist-create',
        })
      );
    }
  };

  const fieldError = errors.name?.message;

  return (
    <ModalContainer size="narrow" testKey="create-benchling-worklist">
      <ModalHeader title="Create Benchling worklist" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3">
          <p className="mb3 f6 mid-gray">
            {animals.length} {animals.length === 1 ? 'animal' : 'animals'} will be added, attributed to you.
          </p>

          <label htmlFor="benchling-worklist-name" className="db mb1 f6">
            Worklist name
          </label>
          <input
            id="benchling-worklist-name"
            type="text"
            className={_isNotBlank(fieldError) ? 'input__error' : ''}
            placeholder="Enter a name for this worklist..."
            autoFocus
            data-testid="create-benchling-worklist__name"
            {...register('name', {
              validate: (value) => (_isNotBlank(value?.trim()) ? true : 'A worklist name is required.'),
            })}
          />
          {_isNotBlank(fieldError) && (
            <small className="red db" data-testid="create-benchling-worklist__error">
              {fieldError}
            </small>
          )}
        </div>

        <ModalActions
          submitBtnText="Create worklist"
          submitButtonProps={{ disabled: isCreating || !isValid, loading: isCreating }}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default CreateBenchlingWorklist;
